import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spinner } from '../../../shared/components/ui';
import { dashboardApi } from '../../../shared/services/dashboardApi';
import { PricingHealthCards } from './PricingHealthCard';
import { CreateEstimateHero } from './CreateEstimateHero';
import { RecentEstimatesTable } from './RecentEstimatesTable';
import { PricingSignals } from './PricingSignals';
import { ReferencedJobs } from './ReferencedJobs';
import { OnboardingChecklist } from './OnboardingChecklist';
import { BusinessTypeStep } from '../../onboarding';

export function Dashboard() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    async function loadDashboardData() {
      try {
        setIsLoading(true);
        setError(null);
        const dashboardData = await dashboardApi.getDashboardData();
        setData(dashboardData);
      } catch (err) {
        console.error('Failed to load dashboard data:', err);
        setError('Failed to load dashboard data. Please try again.');
      } finally {
        setIsLoading(false);
      }
    }

    loadDashboardData();
  }, []);

  const handleHighRiskClick = () => {
    // Navigate to estimates filtered by high risk
    navigate('/estimates?risk=high');
  };

  const handleSignalAction = (signal) => {
    if (signal.linkedQuotes?.length) {
      navigate(`/estimates?ids=${signal.linkedQuotes.join(',')}`);
    } else if (signal.id === 'signal-1') {
      navigate('/settings/pricing');
    }
  };

  const handleViewAllEstimates = () => {
    navigate('/estimates');
  };

  const handleViewAllJobs = () => {
    navigate('/jobs');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <Spinner size="lg" />
          <span className="text-sm text-gray-500 dark:text-gray-400">Loading your pricing intelligence...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-6 text-center">
        <svg className="w-12 h-12 text-red-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <h3 className="text-lg font-medium text-red-800 dark:text-red-300 mb-2">Something went wrong</h3>
        <p className="text-sm text-red-600 dark:text-red-400 mb-4">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 rounded-lg hover:bg-red-200 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  const {
    pricingHealth,
    recentEstimates,
    pricingSignals,
    referencedJobs,
    onboardingState,
    stats,
  } = data || {};

  // Check if user is still onboarding (hasn't completed all steps)
  const isOnboarding = onboardingState && (
    !onboardingState.hasUploadedHistoricalEstimates ||
    !onboardingState.hasCreatedFirstEstimate ||
    !onboardingState.hasAdjustedPricingRules
  );

  // Check if user has historical data
  const hasHistoricalData = stats?.totalEstimates > 0;

  // Business type must be set before the rest of onboarding makes sense.
  // The flag lives on the tenant record (see get_user_onboarding lambda).
  const needsBusinessType = onboardingState && !onboardingState.businessType;

  if (needsBusinessType) {
    return (
      <div className="max-w-7xl mx-auto">
        <BusinessTypeStep
          onComplete={() => {
            // Re-fetch dashboard data so onboardingState picks up the new businessType
            // and the rest of the dashboard renders.
            dashboardApi.getDashboardData().then(setData);
          }}
        />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Onboarding Checklist (first-time users) */}
      {isOnboarding && (
        <OnboardingChecklist state={onboardingState} />
      )}

      {/* Pricing Health Summary */}
      <PricingHealthCards
        data={pricingHealth}
        onHighRiskClick={handleHighRiskClick}
      />

      {/* Create Estimate Hero Action */}
      <CreateEstimateHero hasHistoricalData={hasHistoricalData} />

      {/* Recent Estimates Table */}
      <RecentEstimatesTable
        estimates={recentEstimates || []}
        onViewAll={handleViewAllEstimates}
      />

      {/* Two Column Layout for Signals and Referenced Jobs */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Pricing Signals (1/3 width) */}
        <div className="lg:col-span-1">
          <PricingSignals signals={pricingSignals} onAction={handleSignalAction} />

        </div>

        {/* Referenced Jobs (2/3 width) */}
        <div className="lg:col-span-2">
          <ReferencedJobs
            jobs={referencedJobs}
            onViewAll={handleViewAllJobs}
          />
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
